// Single entry name for both custom-element systems. The implementation
// module is selected at BUILD TIME by the `#ce-entry` alias in
// vite.config.ts (the config/ce_lazy_clients.yml allowlist): eager-boot.js is
// the full-catalog boot, lazy-boot.js the on-demand loader. Views, Sprockets,
// and the Codeload embed channel always reference 'custom_elements'.
import { boot, claimPageOwnership } from "#ce-entry";
import { disposeOmnisearchStore, useOmnisearchStore } from "#ce/apps/shared/store/omnisearch-store";
import { whenMountReady } from "#ce/framework/mount-ready";
import { trimToUndefined } from "#ce/utils/props";

// Page-ownership gate (lazy-only — the eager claim always grants): the
// first lazy bundle to boot owns the page; a losing lazy bundle must not
// reassign the Backbone bridge (a follower bridge would strand legacy
// `configure()` calls in a store nothing reads) and must not boot. Mixed
// eager+lazy pages keep current second-bundle semantics: the eager side
// never consults the token, so eager behavior is unaffected by this gate.
if (claimPageOwnership()) {
  // The single config-handoff boundary for the legacy doctor-finder (Backbone)
  // app, which cannot import ESM modules directly. It calls `configure` once per
  // embed to hand the shared omnisearch store its fetch config + omnisearch
  // options; every consumer (<sp-finder-omnisearch>, the specialty/provider facets) then
  // reads the populated store. This entrypoint is loaded on every public page (see
  // LayoutsHelper), so the global is present in time.
  window.Sparkle = window.Sparkle || {};
  window.Sparkle.DoctorFinderOmnisearch = {
    // Normalize the uuid the same way consumers do (`useOmnisearchStore(trimToUndefined(uuid))`)
    // so a blank-but-present uuid keys the same store on both sides.
    configure: (uuid, config) => useOmnisearchStore(trimToUndefined(uuid)).configure(config),
    // Called from embed teardown to stop polling and release an orphaned embed's store.
    dispose: (uuid) => disposeOmnisearchStore(trimToUndefined(uuid)),
  };

  // The mount-ready await helper for non-module consumers (admin behaviours,
  // legacy doctor-finder scripts) migrating off `customElements.whenDefined`:
  // resolves once the instance's exposed methods are callable, rejects when the
  // instance settled as a terminal load failure. Identical under both entry
  // implementations — the contract is dual-mode. Inside the ownership gate:
  // an inert follower assigns no globals at all.
  window.Sparkle.CustomElements = { ...window.Sparkle.CustomElements, whenMountReady };

  // The Backbone bridge must be assigned before boot() runs.
  boot();
}
